import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { getCategories, getLocations, getPlans } from "@/services/publicApi";
import { getOwnerListingById, saveOwnerListing } from "@/services/ownerApi";
import { firstProblem, validateContactFields } from "@/utils/validation";
import type { Category, Location, Plan } from "@/types";

interface FormState {
  name: string;
  categoryId: string;
  locationId: string;
  description: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  hours: string;
  openNow: boolean;
  tags: string;
  plan: string;
  image: string;
  status: string;
}

const empty: FormState = {
  name: "",
  categoryId: "",
  locationId: "",
  description: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  hours: "",
  openNow: true,
  tags: "",
  plan: "Free",
  image: "",
  status: "pending",
};

const MAX_DESC = 600;

export default function ListingEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { token } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(empty);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([getCategories(), getLocations(), getPlans()])
      .then(([c, l, p]) => {
        setCategories(c);
        setLocations(l);
        setPlans(p);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (isNew) {
      setForm({ ...empty });
      return;
    }
    let active = true;
    setLoading(true);
    getOwnerListingById(String(id), token)
      .then((l) => {
        if (!active || !l) return;
        setForm({
          name: l.name,
          categoryId: l.categoryId,
          locationId: l.locationId,
          description: l.description,
          phone: l.phone,
          email: l.email,
          website: l.website || "",
          address: l.address,
          hours: l.hours,
          openNow: l.openNow,
          tags: l.tags.join(", "),
          plan: l.plan,
          image: l.image,
          status: l.status,
        });
      })
      .catch(() => active && setError("We couldn't load this listing."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, isNew, token]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const contactProblem = firstProblem(
      validateContactFields({ email: form.email, phone: form.phone, emailRequired: true, phoneRequired: true }),
    );
    if (contactProblem) {
      setError(contactProblem);
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      categoryId: form.categoryId,
      locationId: form.locationId,
      description: form.description,
      phone: form.phone,
      email: form.email,
      website: form.website,
      address: form.address,
      hours: form.hours,
      openNow: form.openNow,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      plan: form.plan,
      image: form.image,
    };
    try {
      const ok = await saveOwnerListing(isNew ? "new" : String(id), payload, token);
      if (!ok) {
        setError("We couldn't save your changes. Please try again.");
        return;
      }
      setSaved(true);
      if (isNew) {
        setTimeout(() => navigate("/dashboard/listings"), 900);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        <div className="h-64 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
      </div>
    );
  }

  return (
    <>
      <Seo title={isNew ? "Add Listing" : "Edit Listing"} path="/dashboard/listings" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard/listings"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Back to listings
          </Link>
          <h1 className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
            {isNew ? "Add a new listing" : form.name || "Edit listing"}
          </h1>
        </div>
        {!isNew && <StatusBadge status={form.status} />}
      </div>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Business details</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="name" className={labelClass}>Business name *</label>
                <input id="name" name="name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Chamundi Traders" className={inputClass} />
              </div>
              <div>
                <label htmlFor="categoryId" className={labelClass}>Category *</label>
                <select id="categoryId" name="categoryId" required value={form.categoryId} onChange={(e) => set("categoryId", e.target.value)} className={inputClass}>
                  <option value="" disabled>Select a category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="locationId" className={labelClass}>Location *</label>
                <select id="locationId" name="locationId" required value={form.locationId} onChange={(e) => set("locationId", e.target.value)} className={inputClass}>
                  <option value="" disabled>Select a district</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="description" className={labelClass}>Description *</label>
                  <span className="text-[11px] text-foreground-400">{form.description.length}/{MAX_DESC}</span>
                </div>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={5}
                  maxLength={MAX_DESC}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value.slice(0, MAX_DESC))}
                  placeholder="What do you offer and what makes your business different?"
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Contact &amp; location</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className={labelClass}>Phone *</label>
                <input id="phone" name="phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email *</label>
                <input id="email" name="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="website" className={labelClass}>Website</label>
                <input id="website" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https:// (optional)" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className={labelClass}>Address *</label>
                <input id="address" name="address" required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, area, city, PIN" className={inputClass} />
              </div>
              <div>
                <label htmlFor="hours" className={labelClass}>Opening hours</label>
                <input id="hours" name="hours" value={form.hours} onChange={(e) => set("hours", e.target.value)} placeholder="e.g. 9:00 AM - 8:00 PM" className={inputClass} />
              </div>
              <div>
                <label htmlFor="tags" className={labelClass}>Tags</label>
                <input id="tags" name="tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="comma, separated, tags" className={inputClass} />
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Plan &amp; visibility</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="plan" className={labelClass}>Listing plan</label>
                <select id="plan" name="plan" value={form.plan} onChange={(e) => set("plan", e.target.value)} className={inputClass}>
                  {plans.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="image" className={labelClass}>Cover image URL</label>
                <input id="image" name="image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" className={inputClass} />
              </div>
            </div>
            <label className="mt-4 flex cursor-pointer items-center gap-3">
              <button
                type="button"
                onClick={() => set("openNow", !form.openNow)}
                aria-pressed={form.openNow}
                className={`relative h-6 w-11 rounded-full transition-colors cursor-pointer ${form.openNow ? "bg-accent-500" : "bg-background-400"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-background-50 transition-all ${form.openNow ? "left-[22px]" : "left-0.5"}`}></span>
              </button>
              <span className="text-sm text-foreground-700">Currently open for business</span>
            </label>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Cover preview</h2>
            <div className="mt-3 h-40 w-full overflow-hidden rounded-md bg-background-100">
              {form.image ? (
                <img src={form.image} alt="Cover preview" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-foreground-400">
                  <i className="ri-image-add-line text-3xl"></i>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-foreground-500">
              Paste an image URL above. You can upload more photos from your{" "}
              <Link to="/dashboard/media" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">media library</Link>.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
              <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
              <span>{error}</span>
            </div>
          )}
          {saved && (
            <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-3.5 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600"></i>
              <span>Changes saved successfully.</span>
            </div>
          )}

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              {saving ? "Saving…" : isNew ? "Submit listing" : "Save changes"}
            </button>
            <Link
              to="/dashboard/listings"
              className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </Link>
            {!isNew && form.status === "pending" && (
              <p className="mt-3 text-xs text-foreground-500">
                This listing is pending approval. Re-submitting keeps it in the review queue.
              </p>
            )}
          </div>
        </aside>
      </form>
    </>
  );
}